import { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Mail, Info, Check } from 'lucide-react';
import type { DrawdownGroup } from '@/types/drawdown';
import { cn } from '@/lib/utils';

interface SendEmailsDialogProps {
  open: boolean;
  onClose: () => void;
  drawdown: DrawdownGroup;
  onConfirm: (newOnly: boolean) => void;
  isLoading?: boolean;
}

export const SendEmailsDialog = ({
  open,
  onClose,
  drawdown,
  onConfirm,
  isLoading = false,
}: SendEmailsDialogProps) => {
  const [newOnly, setNewOnly] = useState(true);

  // Calculate counts based on drawdown data
  const totalInvestors = drawdown.totalInvestorCount || 0;
  const sentCount = drawdown.batches.reduce(
    (sum, batch) => sum + (batch.invoicesSent || 0),
    0
  );
  const newInvestorsCount = totalInvestors - sentCount;
  const newInvestorsSuffix = newInvestorsCount !== 1 ? 's' : '';

  // Check for PDF generation failures
  const failedPDFsCount = drawdown.batches.reduce(
    (sum, batch) => sum + (batch.pdfs_failed || 0),
    0
  );
  const hasPDFFailures = failedPDFsCount > 0;

  const handleConfirm = () => {
    onConfirm(newOnly);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Email Notifications</DialogTitle>
          <DialogDescription>
            Send capital call notice emails to investors in Drawdown #
            {drawdown.drawdownNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Select recipients:</p>

            {/* Option 1: Send only new */}
            <button
              type="button"
              onClick={() => setNewOnly(true)}
              className={cn(
                'w-full text-left p-4 rounded-lg border-2 transition-colors',
                newOnly
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">
                      Send only to investors who haven&apos;t received emails
                    </span>
                    {newOnly && <Check className="h-4 w-4 text-primary" />}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {newInvestorsCount > 0
                      ? `${newInvestorsCount} investor${newInvestorsSuffix} will receive emails`
                      : 'All investors have already received emails'}
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Send to all */}
            <button
              type="button"
              onClick={() => setNewOnly(false)}
              className={cn(
                'w-full text-left p-4 rounded-lg border-2 transition-colors',
                !newOnly
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">Resend to all investors</span>
                    {!newOnly && <Check className="h-4 w-4 text-primary" />}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {totalInvestors} investor{totalInvestors !== 1 ? 's' : ''}{' '}
                    will receive emails
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-800 dark:bg-blue-900/20">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-blue-900 dark:text-blue-100">
              <strong>Note:</strong> Emails will contain a brief notification
              with a secure download link to the full PDF notice. The link
              expires in 30 days.
            </div>
          </div>

          {newInvestorsCount === 0 && newOnly && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-amber-900 dark:text-amber-100">
                All investors have already received emails. Select &quot;Resend
                to all investors&quot; to send again.
              </div>
            </div>
          )}

          {hasPDFFailures && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-900/20">
              <Info className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="text-red-900 dark:text-red-100">
                <strong>Cannot send emails:</strong> {failedPDFsCount} PDF
                {failedPDFsCount === 1 ? '' : 's'} failed to generate. Please
                regenerate PDFs before sending emails.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isLoading ||
              hasPDFFailures ||
              (newOnly && newInvestorsCount === 0)
            }
          >
            <Mail className="h-4 w-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send Emails'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
